/** How far each arrow key moves focus along a radio group. Down and right go on, up and left go back. */
const ARROW_STEP: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

type ModifierState = Pick<KeyboardEvent, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>;

/**
 * Whether a modifier key is held. Alt and Cmd with an arrow are browser shortcuts (history back
 * and forward), so the group leaves them alone.
 */
export function hasModifier(event: ModifierState): boolean {
  return [event.altKey, event.ctrlKey, event.metaKey, event.shiftKey].some(Boolean);
}

/**
 * The index focus moves to when `key` is pressed on the radio at `current`, wrapping at both ends.
 * Returns null when the key is not an arrow, or `current` is not a radio of the group.
 */
export function nextRadioIndex(key: string, current: number, count: number): number | null {
  const step = ARROW_STEP[key];
  if (step === undefined || current < 0) {
    return null;
  }
  return (current + step + count) % count;
}

const RADIO_SELECTOR = '[role="radio"]';

/** The radios inside a group element, in the order they appear on the page. */
export function radiosOf(group: HTMLElement): HTMLElement[] {
  return Array.from(group.querySelectorAll<HTMLElement>(RADIO_SELECTOR));
}

/** The position of the radio that holds `target` (or is it), or -1 when `target` is in none. */
export function radioIndexOf(radios: readonly HTMLElement[], target: EventTarget): number {
  const radio = target instanceof Element ? target.closest<HTMLElement>(RADIO_SELECTOR) : null;
  return radio ? radios.indexOf(radio) : -1;
}

export type RadioItem = {
  id: string;
  /** An unavailable item can take focus (so it is announced) but is never a first tab stop. */
  unavailable: boolean;
};

type TabStopInput = {
  items: readonly RadioItem[];
  /** The position of the item with focus now, or null when focus is outside the group. */
  focusedIndex: number | null;
  selectedId: string | null;
};

/**
 * The position of the one item of a radio group that is in the tab order (roving tabindex): the
 * item that has focus, else the chosen one, else the first available one.
 *
 * It is a position, not an id, so the group keeps a single tab stop even if a parent passes ids
 * that repeat. Every item with the chosen id still counts as chosen, and the first of them takes
 * the stop.
 */
export function resolveTabStopIndex({
  items,
  focusedIndex,
  selectedId,
}: Readonly<TabStopInput>): number {
  if (items[focusedIndex ?? -1]) {
    return focusedIndex as number;
  }
  const chosen = items.findIndex((item) => item.id === selectedId && !item.unavailable);
  return chosen >= 0 ? chosen : items.findIndex((item) => !item.unavailable);
}
